import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Chip,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import useHttp from "../hooks/use-http";
import "./Account.css";

const Account = () => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation();

  const [isEditingName, setIsEditingName] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userAccountData, setUserAccountData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const {
    sendRequest: getUserRequest,
    isLoading: getUserLoading,
    error: getUserError,
  } = useHttp();

  function getUser() {
    function applyData(userData) {
      setUserAccountData(userData);
      // Parse name from userData.name
      if (userData?.name) {
        const nameParts = userData.name.split(" ");
        setFirstName(nameParts[0] || "");
        setLastName(nameParts.slice(1).join(" ") || "");
      }
    }

    getUserRequest(
      {
        url: `${process.env.REACT_APP_BACKEND_URL}/user_management/get_user`,
      },
      applyData
    );
  }

  const handleEditName = () => {
    setIsEditingName(true);
    if (userAccountData?.name) {
      const nameParts = userAccountData.name.split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
    }
  };

  const handleSaveName = () => {
    // TODO: Implement API call to update user name
    console.log("Saving name:", { firstName, lastName });
    setIsEditingName(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    if (userAccountData?.name) {
      const nameParts = userAccountData.name.split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
    }
  };

  const handleDeleteAccount = () => {
    const expectedText = t("deleteAccountPlaceholder").toLowerCase();
    if (confirmDeleteText.toLowerCase() === expectedText) {
      // TODO: Implement API call to delete account
      console.log("Deleting account for user:", user?.email);
      logout({ returnTo: window.location.origin });
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setConfirmDeleteText("");
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setConfirmDeleteText("");
  };

  useEffect(() => {
    getUser();
  }, []);

  return (
    <div
      className="main-container"
      style={{ paddingTop: "24px", paddingBottom: "24px" }}
    >
      <div className="nc-form">
        <div className="nc-form-input">
          <span className="nc-form-title">{t("accountSettings")}</span>
          <span className="nc-form-subtitle">{t("managePersonalInfo")}</span>
          <Divider sx={{ width: "100%", marginBottom: "24px" }} />

          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {t("dataSuccessfullySaved")}
            </Alert>
          )}

          {getUserError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getUserError}
            </Alert>
          )}

          <div
            className="nc-form-main"
            style={{ flexDirection: "column", gap: "24px" }}
          >
            <div className="nc-inputs">
              <span className="nc-inputs-title">
                <AccountCircleIcon sx={{ color: "#439BD9" }} />
                {t("accountInfo")}
              </span>

              {getUserLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "32px",
                    backgroundColor: "#F0F7FC",
                    border: "1px solid #DCEDFA",
                    borderRadius: "16px",
                    marginTop: "8px",
                    maxWidth: "500px",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    padding: "16px",
                    backgroundColor: "#F0F7FC",
                    border: "1px solid #DCEDFA",
                    borderRadius: "16px",
                    marginTop: "8px",
                    maxWidth: "500px",
                  }}
                >
                  <div>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6B7280",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {t("accountCreated")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#0F3A58",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      {userAccountData?.createdOn
                        ? new Date(
                            userAccountData.createdOn
                          ).toLocaleDateString("nl-NL")
                        : t("unknown")}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6B7280",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {t("lastLogin")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#0F3A58",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      {userAccountData?.lastLogin
                        ? new Date(
                            userAccountData.lastLogin
                          ).toLocaleDateString("nl-NL")
                        : t("unknown")}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6B7280",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      Abonnement
                    </Typography>
                    <div style={{ marginTop: "4px" }}>
                      <Chip
                        label="PREMIUM"
                        sx={{
                          backgroundColor: "#10B981",
                          color: "#FFFFFF",
                          fontSize: "11px",
                          fontWeight: 700,
                          height: "24px",
                          "& .MuiChip-label": {
                            paddingX: "8px",
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6B7280",
                          fontSize: "11px",
                          marginLeft: "8px",
                          fontStyle: "italic",
                        }}
                      >
                        ({t("temporaryFree")})
                      </Typography>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="nc-inputs mt-1">
              <span className="nc-inputs-title">
                <PersonIcon sx={{ color: "#439BD9" }} />
                {t("personalInfo")}
                {!isEditingName && (
                  <span
                    onClick={handleEditName}
                    style={{
                      color: "#439BD9",
                      cursor: "pointer",
                      marginLeft: "8px",
                      fontSize: "14px",
                      textDecoration: "underline",
                    }}
                  >
                    {t("Bewerken")}
                  </span>
                )}
              </span>

              {getUserLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "32px",
                    maxWidth: "400px",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <div className="nc-textfields" style={{ maxWidth: "400px" }}>
                  <TextField
                    label={t("firstName")}
                    variant="outlined"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={!isEditingName}
                    required
                    sx={{
                      backgroundColor: "#F9FCFE",
                      border: "1px solid #ECF5FD",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: !isEditingName ? "#F5F5F5" : "#F9FCFE",
                      },
                    }}
                  />
                  <TextField
                    label={t("lastName")}
                    variant="outlined"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={!isEditingName}
                    required
                    sx={{
                      backgroundColor: "#F9FCFE",
                      border: "1px solid #ECF5FD",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: !isEditingName ? "#F5F5F5" : "#F9FCFE",
                      },
                    }}
                  />
                </div>
              )}

              <div className="nc-textfields" style={{ maxWidth: "400px" }}>
                <TextField
                  label={t("emailAddress")}
                  variant="outlined"
                  value={user?.email || ""}
                  disabled
                  helperText={t("emailCannotBeChanged")}
                  sx={{
                    backgroundColor: "#F5F5F5",
                    border: "1px solid #E0E0E0",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                />
              </div>

              {isEditingName && (
                <div
                  style={{ display: "flex", gap: "12px", marginTop: "16px" }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveName}
                    sx={{
                      color: "#fff",
                      borderRadius: "40px",
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    {t("save")}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    sx={{
                      color: "#439BD9",
                      backgroundColor: "#D7E9F7",
                      borderRadius: "40px",
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "#C1DDF5",
                      },
                    }}
                  >
                    {t("cancel")}
                  </Button>
                </div>
              )}
            </div>

            {!getUserLoading && !getUserError && (
              <div className="nc-inputs mt-1">
                <span className="nc-inputs-title" style={{ color: "#EF4444" }}>
                  <DeleteIcon sx={{ color: "#EF4444" }} />
                  {t("dangerZone")}
                </span>

                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: "16px",
                    marginTop: "8px",
                    maxWidth: "500px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#000",
                      fontSize: "14px",
                      lineHeight: "1.6",
                      marginBottom: "16px",
                    }}
                  >
                    {t("deleteAccountWarning")}
                  </Typography>

                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={openDeleteDialog}
                    sx={{
                      borderRadius: "40px",
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#EF4444",
                      color: "#EF4444",
                      "&:hover": {
                        backgroundColor: "#FEF2F2",
                        borderColor: "#DC2626",
                      },
                    }}
                  >
                    {t("deleteAccount")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "error.main",
            fontWeight: 700,
            fontSize: "20px",
            position: "relative",
            paddingBottom: "16px",
          }}
        >
          {t("deleteAccountConfirmTitle")}
          <CloseIcon
            onClick={closeDeleteDialog}
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              cursor: "pointer",
              color: "#9CA3AF",
              fontSize: "24px",
              "&:hover": {
                color: "#6B7280",
              },
            }}
          />
          <Divider
            sx={{
              position: "absolute",
              bottom: 0,
              left: "16px",
              right: "16px",
              width: "calc(100% - 32px)",
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ px: 3, maxWidth: "500px", minWidth: "300px" }}>
          <DialogContentText
            sx={{
              mb: 3,
              pt: "12px",
              fontSize: "16px",
              lineHeight: "1.5",
              color: "#021929",
            }}
          >
            {t("deleteAccountConfirmText")}
          </DialogContentText>
          <DialogContentText
            sx={{
              mb: 2,
              fontSize: "16px",
              lineHeight: "1.5",
              color: "#021929",
            }}
          >
            {t("deleteAccountInstructions")}
          </DialogContentText>
          <TextField
            fullWidth
            value={confirmDeleteText}
            onChange={(e) => setConfirmDeleteText(e.target.value)}
            placeholder={t("deleteAccountPlaceholder")}
            variant="outlined"
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={closeDeleteDialog}
            variant="contained"
            sx={{
              color: "#439BD9",
              backgroundColor: "#D7E9F7",
              borderRadius: "40px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#C1DDF5",
              },
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={
              confirmDeleteText.toLowerCase() !==
              t("deleteAccountPlaceholder").toLowerCase()
            }
            sx={{
              borderRadius: "40px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              backgroundColor: "#EF4444",
              "&:hover": {
                backgroundColor: "#DC2626",
              },
              "&:disabled": {
                backgroundColor: "#F5F5F5",
                color: "#9CA3AF",
              },
            }}
          >
            {t("deleteAccount")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Account;
